import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import EkgStrip from "@/components/EkgStrip";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Zap, Skull, Trophy, ChevronLeft, RotateCcw } from "lucide-react";

// Rhythm bank — all four choices are always plausible clinical calls
const RHYTHMS = [
  { key: "sinus", label: "Normal Sinus Rhythm" },
  { key: "afib", label: "Atrial Fibrillation" },
  { key: "aflutter", label: "Atrial Flutter" },
  { key: "svt", label: "SVT" },
  { key: "vtach", label: "Ventricular Tachycardia" },
  { key: "stemi", label: "STEMI (ST Elevation)" },
  { key: "lbbb", label: "Left Bundle Branch Block" },
  { key: "rbbb", label: "Right Bundle Branch Block" },
  { key: "wenckebach", label: "Wenckebach (Mobitz I)" },
];

const START_TIME_MS = 10000;
const MIN_TIME_MS = 3500;
const TIME_DROP_PER_ROUND = 400; // ms
const CORRECT_BASE = 100;

function sampleOptions(correctKey) {
  const others = RHYTHMS.filter((r) => r.key !== correctKey);
  // shuffle
  for (let i = others.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [others[i], others[j]] = [others[j], others[i]];
  }
  const picks = others.slice(0, 3);
  const correct = RHYTHMS.find((r) => r.key === correctKey);
  const options = [...picks, correct];
  for (let i = options.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [options[i], options[j]] = [options[j], options[i]];
  }
  return options;
}

function pickRhythm(excludeKey) {
  const pool = RHYTHMS.filter((r) => r.key !== excludeKey);
  return pool[Math.floor(Math.random() * pool.length)];
}

export default function CodeBlue() {
  const { user, refresh } = useAuth();
  const [stage, setStage] = useState("intro"); // intro | playing | gameover
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [current, setCurrent] = useState(null);
  const [options, setOptions] = useState([]);
  const [timeLeft, setTimeLeft] = useState(START_TIME_MS);
  const [maxTime, setMaxTime] = useState(START_TIME_MS);
  const [feedback, setFeedback] = useState(null); // { correct, correctKey } | null
  const [locked, setLocked] = useState(false);
  const [finalPayload, setFinalPayload] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  const tickerRef = useRef(null);
  const roundStartRef = useRef(0);

  useEffect(() => {
    api.get("/code-blue/leaderboard").then(({ data }) => setLeaderboard(data.leaderboard));
    return () => tickerRef.current && clearInterval(tickerRef.current);
  }, []);

  const startGame = () => {
    setStage("playing");
    setRound(0);
    setScore(0);
    setFeedback(null);
    nextRound(0, 0);
  };

  const nextRound = (r, s, lastKey = null) => {
    const nextR = r + 1;
    const rhythm = pickRhythm(lastKey);
    const opts = sampleOptions(rhythm.key);
    const roundTime = Math.max(MIN_TIME_MS, START_TIME_MS - nextR * TIME_DROP_PER_ROUND);
    setRound(nextR);
    setScore(s);
    setCurrent(rhythm);
    setOptions(opts);
    setMaxTime(roundTime);
    setTimeLeft(roundTime);
    setFeedback(null);
    setLocked(false);
    roundStartRef.current = Date.now();

    if (tickerRef.current) clearInterval(tickerRef.current);
    tickerRef.current = setInterval(() => {
      const elapsed = Date.now() - roundStartRef.current;
      const left = roundTime - elapsed;
      if (left <= 0) {
        clearInterval(tickerRef.current);
        setTimeLeft(0);
        handleTimeout();
      } else {
        setTimeLeft(left);
      }
    }, 100);
  };

  const handleTimeout = () => {
    if (locked) return;
    setLocked(true);
    setFeedback({ correct: false, correctKey: current.key, reason: "timeout" });
    setTimeout(() => finishGame(score), 1400);
  };

  const pick = (optKey) => {
    if (locked) return;
    if (tickerRef.current) clearInterval(tickerRef.current);
    setLocked(true);
    const isRight = optKey === current.key;
    if (isRight) {
      const elapsed = Date.now() - roundStartRef.current;
      const timeBonus = Math.round(Math.max(0, (maxTime - elapsed) / maxTime) * 100);
      const gained = CORRECT_BASE + timeBonus;
      const newScore = score + gained;
      setFeedback({ correct: true, correctKey: current.key, gained });
      setTimeout(() => nextRound(round, newScore, current.key), 900);
    } else {
      setFeedback({ correct: false, correctKey: current.key });
      setTimeout(() => finishGame(score), 1400);
    }
  };

  const finishGame = async (finalScore) => {
    if (tickerRef.current) clearInterval(tickerRef.current);
    setStage("gameover");
    try {
      const { data } = await api.post("/code-blue/score", {
        score: finalScore,
        rounds: round - 1, // last round didn't count (miss)
      });
      setFinalPayload(data);
      if (data.new_high_score) toast.success(`New high score! ${data.high_score}`);
      await refresh();
      api.get("/code-blue/leaderboard").then(({ data }) => setLeaderboard(data.leaderboard));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const timePct = Math.max(0, Math.min(100, (timeLeft / maxTime) * 100));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Navbar />

      <div className="border-b border-white/5 bg-black/40">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-red-400 flex items-center gap-2">
              <Skull className="w-3 h-3" /> CODE BLUE · ARCADE MODE
            </div>
            <h1 className="font-display text-2xl font-bold mt-1">Rhythm Rescue</h1>
          </div>
          <Link
            to="/dashboard"
            className="text-sm text-slate-400 hover:text-white flex items-center gap-1"
            data-testid="code-blue-back"
          >
            <ChevronLeft className="w-4 h-4" /> Dojo
          </Link>
        </div>
      </div>

      {stage === "intro" && (
        <div className="max-w-3xl mx-auto p-6 lg:p-10">
          <div className="reveal">
            <div className="p-8 rounded-lg border border-red-500/30 bg-red-500/5 glow-red">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-6 h-6 text-red-400" />
                <div className="font-mono text-xs uppercase tracking-widest text-red-400">
                  Rules
                </div>
              </div>
              <ul className="space-y-2 text-slate-200 text-sm leading-relaxed">
                <li>• A rhythm strip flashes. Identify it before the timer hits zero.</li>
                <li>• Every wrong answer or timeout ends the game.</li>
                <li>• Correct answers earn 100 pts + a speed bonus (max 100).</li>
                <li>• The timer shrinks every round. Stay sharp.</li>
              </ul>
            </div>

            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              <Stat
                label="Your High"
                value={user?.code_blue_high_score || 0}
                testid="code-blue-high"
              />
              <Stat
                label="Best Streak"
                value={user?.code_blue_best_rounds || 0}
                testid="code-blue-best-rounds"
              />
              <Stat
                label="Plays"
                value={user?.code_blue_plays || 0}
                testid="code-blue-plays"
              />
            </div>

            <div className="mt-8 flex justify-center">
              <Button
                onClick={startGame}
                className="rounded-full bg-red-500 hover:bg-red-400 text-white font-semibold px-10 h-12 glow-red text-base"
                data-testid="code-blue-start-btn"
              >
                Start Code Blue
              </Button>
            </div>

            {leaderboard.length > 0 && (
              <div className="mt-12">
                <div className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">
                  Top 10 Ninjas
                </div>
                <div className="rounded-md border border-white/10 divide-y divide-white/5" data-testid="code-blue-leaderboard">
                  {leaderboard.map((row, i) => (
                    <div
                      key={`${row.name}-${i}`}
                      className="flex items-center justify-between px-4 py-2.5 text-sm"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-slate-500 w-6">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="text-slate-200">{row.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-mono text-xs text-slate-500">
                          {row.rounds}R
                        </span>
                        <span className="font-mono text-green-400 font-semibold">
                          {row.score}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {stage === "playing" && current && (
        <div className="max-w-4xl mx-auto p-6 lg:p-10">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-xs uppercase tracking-widest text-slate-500">
              Round {round}
            </div>
            <div className="flex items-center gap-6">
              <div className="font-mono text-xs text-slate-500">
                SCORE{" "}
                <span className="text-green-400 font-bold ml-1" data-testid="code-blue-score">
                  {score}
                </span>
              </div>
              <div className="font-mono text-xs text-slate-500" data-testid="code-blue-time">
                {(timeLeft / 1000).toFixed(1)}s
              </div>
            </div>
          </div>

          {/* Timer bar */}
          <div className="h-2 bg-white/5 rounded-full overflow-hidden mb-6">
            <div
              className={`h-full transition-all duration-100 ease-linear ${
                timePct > 50 ? "bg-green-400" : timePct > 25 ? "bg-yellow-400" : "bg-red-500"
              }`}
              style={{ width: `${timePct}%` }}
              data-testid="code-blue-timer-bar"
            />
          </div>

          <div
            className={`rounded-lg border overflow-hidden reveal transition-colors ${
              feedback?.correct
                ? "border-green-500/60 glow-green"
                : feedback && !feedback.correct
                ? "border-red-500/60 glow-red"
                : "border-green-500/30"
            }`}
            data-testid="code-blue-strip"
          >
            <div className="px-4 py-2 border-b border-green-500/20 bg-green-500/5 flex items-center justify-between">
              <div className="font-mono text-xs text-green-400">
                LEAD II · IDENTIFY THE RHYTHM
              </div>
              <div className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            </div>
            <div className="h-[300px]">
              <EkgStrip rhythm={current.key} />
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {options.map((opt) => {
              const isCorrect = feedback && opt.key === feedback.correctKey;
              const isChosenWrong =
                feedback && !feedback.correct && opt.key !== feedback.correctKey && locked;
              return (
                <button
                  key={opt.key}
                  onClick={() => pick(opt.key)}
                  disabled={locked}
                  className={`p-4 rounded-md border text-left transition-colors ${
                    isCorrect
                      ? "border-green-500/70 bg-green-500/15"
                      : isChosenWrong
                      ? "border-white/10 bg-white/[0.02] opacity-40"
                      : "border-white/15 bg-white/[0.03] hover:border-red-500/50 hover:bg-red-500/5"
                  } disabled:cursor-not-allowed`}
                  data-testid={`code-blue-option-${opt.key}`}
                >
                  <span className="font-display font-semibold">{opt.label}</span>
                </button>
              );
            })}
          </div>

          {feedback && (
            <div
              className={`mt-5 text-center font-mono text-sm ${
                feedback.correct ? "text-green-400" : "text-red-400"
              }`}
              data-testid="code-blue-feedback"
            >
              {feedback.correct
                ? `+${feedback.gained} pts`
                : feedback.reason === "timeout"
                ? "Timeout — game over"
                : `Wrong — it was ${
                    RHYTHMS.find((r) => r.key === feedback.correctKey)?.label
                  }`}
            </div>
          )}
        </div>
      )}

      {stage === "gameover" && (
        <div className="max-w-2xl mx-auto p-6 lg:p-10 text-center">
          <div className="reveal">
            <div className="w-24 h-24 mx-auto mb-6 clip-hex bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center">
              <Trophy className="w-10 h-10 text-black" />
            </div>
            <div className="font-mono text-xs uppercase tracking-widest text-red-400 mb-2">
              Game Over
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold" data-testid="code-blue-final-score">
              {finalPayload?.score ?? score} pts
            </h1>
            <p className="text-slate-300 mt-3">
              You survived <span className="text-green-400">{finalPayload?.rounds ?? 0}</span>{" "}
              rounds.
            </p>
            {finalPayload?.new_high_score ? (
              <div className="mt-4 inline-block px-4 py-1.5 rounded-full border border-green-500/40 bg-green-500/10 text-green-400 font-mono text-xs uppercase tracking-widest">
                New personal best!
              </div>
            ) : (
              <div className="mt-4 text-sm text-slate-400">
                Your high score: {finalPayload?.high_score ?? user?.code_blue_high_score ?? 0}
              </div>
            )}

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button
                onClick={startGame}
                className="rounded-full bg-red-500 hover:bg-red-400 text-white font-semibold px-6"
                data-testid="code-blue-play-again"
              >
                <RotateCcw className="w-4 h-4 mr-2" /> Play again
              </Button>
              <Link to="/dashboard">
                <Button
                  variant="outline"
                  className="rounded-full border-white/20 bg-white/5 hover:bg-white/10 text-white"
                  data-testid="code-blue-return-dojo"
                >
                  Back to Dojo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="p-4 rounded-md border border-white/10 bg-white/[0.03]" data-testid={testid}>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">
        {label}
      </div>
      <div className="font-display text-2xl font-bold">{value}</div>
    </div>
  );
}
